import React, { useState, useEffect } from "react";
import axios from "axios";
import countryData from "../data/country.json";
import { db, auth } from "../firebase";
import {
    collection,
    getDocs,
    query,
    where,
    serverTimestamp,
    doc,
    getDoc,
    setDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const AddPackageModal = ({ fetchPackages, countries = [] }) => {
    const [formData, setFormData] = useState({
        countryCode: "",
        operatorId: "",
        recipientAmount: "",
        recipientCurrency: "",
        basePriceGbp: "",
        type: "airtime",
        enabled: true,
        exchange: "Reloadly",
        note: "",
    });

    const [userId, setUserId] = useState(null);
    const [successMsg, setSuccessMsg] = useState("");
    const [operators, setOperators] = useState([]);
    const [showExchangeBy, setShowExchangeBy] = useState(false);
    const [globalExchangeData, setGlobalExchangeData] = useState(null);
    const [selectedCountryData, setSelectedCountryData] = useState(null);
    const [loadingOperators, setLoadingOperators] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) setUserId(user.uid);
        });
        return () => unsubscribe();
    }, []);

    // ── Auto-generated display name ──
    const displayName =
        formData.recipientAmount && formData.recipientCurrency
            ? `${formData.recipientAmount} ${formData.recipientCurrency.toUpperCase()}`
            : "";

    // ── Check if global exchange exists for country ──
    const checkGlobalExchange = async (countryCode) => {
        try {
            const q = query(
                collection(db, "globalExchange"),
                where("isoCode", "==", countryCode)
            );
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                const exchangeData = snapshot.docs[0].data();
                setGlobalExchangeData(exchangeData);
                setShowExchangeBy(true);
                setFormData((prev) => ({ ...prev, exchange: "Reloadly" }));
                return true;
            } else {
                setGlobalExchangeData(null);
                setShowExchangeBy(false);
                return false;
            }
        } catch (error) {
            console.error("Error checking global exchange:", error);
            setShowExchangeBy(false);
            return false;
        }
    };

    // ── Fetch operators (existing API logic preserved) ──
    const fetchOperators = async (countryCode) => {
        try {
            setLoadingOperators(true);
            const user = auth.currentUser;
            if (!user) {
                setLoadingOperators(false);
                return;
            }
            const idToken = await user.getIdToken();
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/fetchOperators/${countryCode}?size=200`,
                {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (response.data.success) {
                const ops = response.data.data || [];
                setOperators(ops);
                // Auto-select first operator if only one exists
                if (ops.length === 1) {
                    setFormData((prev) => ({
                        ...prev,
                        operatorId: ops[0].id.toString(),
                    }));
                }
            } else {
                console.error(
                    "Failed to fetch operators:",
                    response.data.error
                );
                setOperators([]);
            }
        } catch (error) {
            console.error("Error fetching operators:", error);
            setOperators([]);
        } finally {
            setLoadingOperators(false);
        }
    };

    // ── Fetch FX rate from Reloadly (existing API logic preserved) ──
    const fetchFxRate = async (operatorId, amount) => {
        try {
            const user = auth.currentUser;
            if (!user) return;
            const idToken = await user.getIdToken();
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/fetchFxRate`,
                {
                    operatorId: parseInt(operatorId),
                    amount: parseFloat(amount),
                    currencyCode: "GBP",
                },
                {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (response.data.success) {
                const { fxRate, currencyCode } = response.data.data;
                setFormData((prev) => ({
                    ...prev,
                    recipientAmount: fxRate.toString(),
                    recipientCurrency: currencyCode,
                }));
            } else {
                console.error(
                    "Failed to fetch FX rate:",
                    response.data.error
                );
            }
        } catch (error) {
            console.error("Error fetching FX rate:", error);
        }
    };

    // ── Calculate custom FX rate from global exchange data ──
    const calculateCustomFxRate = (basePriceGbp) => {
        if (
            globalExchangeData &&
            globalExchangeData.fxAmount &&
            selectedCountryData
        ) {
            const customRate =
                parseFloat(basePriceGbp) *
                parseFloat(globalExchangeData.fxAmount);
            const countryInfo = countryData.find(
                (c) => c.isoCode === selectedCountryData.code
            );
            setFormData((prev) => ({
                ...prev,
                recipientAmount: customRate.toFixed(2),
                recipientCurrency:
                    countryInfo?.currency || prev.recipientCurrency,
            }));
        }
    };

    // ── Handle form field changes ──
    const handleChange = async (e) => {
        const { name, value, type, checked } = e.target;

        // Handle enabled toggle
        if (type === "checkbox" && name === "enabled") {
            setFormData((prev) => ({ ...prev, enabled: checked }));
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));

        // Country changed → load operators + set default currency
        if (name === "countryCode") {
            const selectedCountry = countries.find((c) => c.code === value);
            if (selectedCountry) {
                setSelectedCountryData(selectedCountry);
                const countryInfo = countryData.find(
                    (c) => c.isoCode === value
                );
                setFormData((prev) => ({
                    ...prev,
                    countryCode: value,
                    recipientCurrency: countryInfo?.currency || "",
                    operatorId: "",
                }));
                await fetchOperators(value);
                await checkGlobalExchange(value);
            }
            return;
        }

        // Base price changed → auto-calculate recipient amount
        if (name === "basePriceGbp") {
            let price = parseFloat(value);
            if (isNaN(price)) price = 0;
            if (price < 0) price = 0;

            setFormData((prev) => ({
                ...prev,
                basePriceGbp: price.toString(),
            }));

            const { operatorId, exchange } = formData;
            if (operatorId && exchange === "Reloadly") {
                fetchFxRate(operatorId, price);
            } else if (exchange === "Custom" && globalExchangeData) {
                calculateCustomFxRate(price);
            }
            return;
        }

        // Operator changed → recalculate if base price exists
        if (name === "operatorId" && formData.basePriceGbp) {
            if (formData.exchange === "Reloadly") {
                fetchFxRate(value, formData.basePriceGbp);
            } else if (
                formData.exchange === "Custom" &&
                globalExchangeData
            ) {
                calculateCustomFxRate(formData.basePriceGbp);
            }
        }

        // Exchange type changed → recalculate
        if (name === "exchange") {
            if (
                value === "Reloadly" &&
                formData.operatorId &&
                formData.basePriceGbp
            ) {
                fetchFxRate(formData.operatorId, formData.basePriceGbp);
            } else if (
                value === "Custom" &&
                globalExchangeData &&
                formData.basePriceGbp
            ) {
                calculateCustomFxRate(formData.basePriceGbp);
            }
        }
    };

    // ── Generate unique packageId ──
    const generatePackageId = () => {
        const num = Date.now();
        const shortId = Math.random().toString(36).substring(2, 6);
        return `pkg_${num}_${shortId}`;
    };

    // ── Reset form to defaults ──
    const resetForm = () => {
        setFormData({
            countryCode: "",
            operatorId: "",
            recipientAmount: "",
            recipientCurrency: "",
            basePriceGbp: "",
            type: "airtime",
            enabled: true,
            exchange: "Reloadly",
            note: "",
        });
        setOperators([]);
        setLoadingOperators(false);
        setShowExchangeBy(false);
        setGlobalExchangeData(null);
        setSelectedCountryData(null);
    };

    // ── Submit handler ──
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userId) return alert("User not authenticated.");

        // Validation
        const recipientAmt = parseFloat(formData.recipientAmount);
        const basePrice = parseFloat(formData.basePriceGbp);

        if (!formData.countryCode || !formData.operatorId) {
            return alert("Please select country and operator.");
        }
        if (isNaN(recipientAmt) || recipientAmt <= 0) {
            return alert("Recipient Amount must be greater than 0.");
        }
        if (!formData.recipientCurrency.trim()) {
            return alert("Recipient Currency is required.");
        }
        if (isNaN(basePrice) || basePrice <= 0) {
            return alert("Base Price (GBP) must be greater than 0.");
        }

        try {
            setSaving(true);
            const operator = operators.find(
                (op) => op.id.toString() === formData.operatorId
            );
            const generatedDisplayName = `${recipientAmt} ${formData.recipientCurrency.toUpperCase()}`;

            const packageId = generatePackageId();

            // Check if document with same packageId already exists
            const packageDocRef = doc(db, "packages", packageId);
            // const packageDocSnap = await getDoc(packageDocRef);
            // if (packageDocSnap.exists()) {
            //     return alert("❌ Error: A package with this ID already exists.");
            // }

            const payload = {
                packageId: packageId,
                countryCode: formData.countryCode,
                operatorId: parseInt(formData.operatorId, 10),
                operatorName: operator?.name || "",
                displayName: generatedDisplayName,
                recipientAmount: recipientAmt,
                recipientCurrency: formData.recipientCurrency.toUpperCase(),
                basePriceGbp: basePrice,
                type: formData.type,
                enabled: formData.enabled,
                exchange: formData.exchange.toLowerCase(),
                note: formData.note,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            await setDoc(packageDocRef, payload);

            setSuccessMsg("✅ Package added successfully!");
            resetForm();

            // Close the Bootstrap modal
            const modalEl = document.getElementById("addPackageModal");
            const bsModal =
                window.bootstrap?.Modal?.getInstance(modalEl);
            if (bsModal) bsModal.hide();

            if (fetchPackages) fetchPackages();
            setTimeout(() => setSuccessMsg(""), 3000);
        } catch (error) {
            console.error("Error adding package:", error);
            alert("❌ Failed to add package.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal fade" id="addPackageModal" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content rounded-4 shadow">
                    <div className="modal-header border-0">
                        <h5 className="text-h1 fw-bold text-primary w-100 text-center mt-3">
                            Add New Package
                        </h5>
                        <button
                            type="button"
                            className="btn-close position-absolute end-0 top-0 m-3"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                        ></button>
                    </div>

                    <div className="modal-body px-4 pb-3">
                        {successMsg && (
                            <div className="alert alert-success">
                                {successMsg}
                            </div>
                        )}
                        <form onSubmit={handleSubmit}>
                            {/* ── Country ── */}
                            <div className="mb-2">
                                <label className="form-label fw-semibold">
                                    Country *
                                </label>
                                <select
                                    name="countryCode"
                                    className="form-select"
                                    value={formData.countryCode}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Country</option>
                                    {countries.map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* ── Operator ── */}
                            <div className="mb-2">
                                <label className="form-label fw-semibold">
                                    Operator *
                                </label>
                                <select
                                    name="operatorId"
                                    className="form-select"
                                    value={formData.operatorId}
                                    onChange={handleChange}
                                    disabled={
                                        !formData.countryCode ||
                                        loadingOperators
                                    }
                                    required
                                >
                                    <option value="">
                                        {!formData.countryCode
                                            ? "Select Country First"
                                            : loadingOperators
                                            ? "Loading operators..."
                                            : "Select Operator"}
                                    </option>
                                    {operators.map((op) => (
                                        <option key={op.id} value={op.id}>
                                            {op.name}
                                        </option>
                                    ))}
                                </select>
                                {loadingOperators && (
                                    <div className="text-muted small mt-1">
                                        <span
                                            className="admin-loader-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Fetching operators...
                                    </div>
                                )}
                            </div>

                            {/* ── Exchange By (only if global exchange exists) ── */}
                            {showExchangeBy && (
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">
                                        Exchange By
                                    </label>
                                    <div className="d-flex gap-3">
                                        {["Reloadly", "Custom"].map(
                                            (exchange) => (
                                                <div
                                                    className="form-check"
                                                    key={exchange}
                                                >
                                                    <input
                                                        type="radio"
                                                        className="form-check-input"
                                                        name="exchange"
                                                        value={exchange}
                                                        id={`add-exchange-${exchange}`}
                                                        checked={
                                                            formData.exchange ===
                                                            exchange
                                                        }
                                                        onChange={handleChange}
                                                    />
                                                    <label
                                                        className="form-check-label"
                                                        htmlFor={`add-exchange-${exchange}`}
                                                    >
                                                        {exchange}
                                                    </label>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* ── Base Price (GBP) ── */}
                            <div className="mb-2">
                                <label className="form-label fw-semibold">
                                    Base Price (GBP) *
                                </label>
                                <input
                                    type="number"
                                    name="basePriceGbp"
                                    className="form-control"
                                    value={formData.basePriceGbp}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    placeholder="Enter base price in GBP"
                                    required
                                />
                            </div>

                            {/* ── Recipient Amount ── */}
                            <div className="mb-2">
                                <label className="form-label fw-semibold">
                                    Recipient Amount *{" "}
                                    {formData.recipientCurrency &&
                                        `(${formData.recipientCurrency.toUpperCase()})`}
                                </label>
                                <input
                                    type="number"
                                    name="recipientAmount"
                                    className="form-control"
                                    value={formData.recipientAmount}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    placeholder="Enter recipient amount"
                                    required
                                />
                                {formData.exchange === "Reloadly" &&
                                    formData.recipientAmount &&
                                    formData.operatorId && (
                                        <div className="text-info small mt-1 fst-italic">
                                            ✨ Auto-calculated from Reloadly FX
                                            rate
                                        </div>
                                    )}
                                {formData.exchange === "Custom" &&
                                    formData.recipientAmount &&
                                    globalExchangeData && (
                                        <div className="text-info small mt-1 fst-italic">
                                            ✨ Auto-calculated from Custom
                                            exchange rate
                                        </div>
                                    )}
                            </div>

                            {/* ── Recipient Currency ── */}
                            <div className="mb-2">
                                <label className="form-label fw-semibold">
                                    Recipient Currency *
                                </label>
                                <input
                                    type="text"
                                    name="recipientCurrency"
                                    className="form-control"
                                    value={formData.recipientCurrency}
                                    onChange={handleChange}
                                    placeholder="e.g. AFN, BDT, PKR, TRY"
                                    required
                                />
                                <small className="text-muted">
                                    This is the currency the recipient receives.
                                </small>
                            </div>

                            {/* ── Type ── */}
                            <div className="mb-2">
                                <label className="form-label fw-semibold">
                                    Type *
                                </label>
                                <select
                                    name="type"
                                    className="form-select"
                                    value={formData.type}
                                    onChange={handleChange}
                                >
                                    <option value="airtime">Airtime</option>
                                    <option value="data">Data</option>
                                </select>
                            </div>

                            {/* ── Enabled Toggle ── */}
                            <div className="mb-2">
                                <label className="form-label fw-semibold">
                                    Status
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                    <div className="form-check form-switch mb-0">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            name="enabled"
                                            checked={formData.enabled}
                                            onChange={handleChange}
                                            style={{
                                                width: "2.5em",
                                                height: "1.25em",
                                            }}
                                        />
                                    </div>
                                    <span
                                        className={`fw-semibold ${
                                            formData.enabled
                                                ? "text-success"
                                                : "text-danger"
                                        }`}
                                    >
                                        {formData.enabled
                                            ? "Enabled"
                                            : "Disabled"}
                                    </span>
                                </div>
                            </div>

                            {/* ── Note ── */}
                            <div className="mb-2">
                                <label className="form-label fw-semibold">
                                    Note
                                </label>
                                <textarea
                                    name="note"
                                    className="form-control"
                                    value={formData.note}
                                    onChange={handleChange}
                                    rows="2"
                                    placeholder="Optional note"
                                />
                            </div>

                            {/* ── Display Name Preview ── */}
                            {displayName && (
                                <div
                                    className="mb-3 p-2 rounded"
                                    style={{
                                        backgroundColor:
                                            "rgba(217, 255, 31, 0.08)",
                                        border: "1px dashed var(--accent-lime)",
                                    }}
                                >
                                    <small className="d-block" style={{ color: 'var(--text-secondary)' }}>
                                        Display Name (auto-generated)
                                    </small>
                                    <strong style={{ color: 'var(--accent-lime)' }}>
                                        {displayName}
                                    </strong>
                                </div>
                            )}

                            <div className="d-grid mt-3">
                                <button
                                    type="submit"
                                    className="btn btn-primary py-1 mb-3"
                                    disabled={saving}
                                >
                                    {saving ? "Adding..." : "Add Package"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddPackageModal;